import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { AlertTriangle, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Deliverable, DeliverableStatus } from '@/hooks/useDeliverables';
import { useNavigate } from 'react-router-dom';

// Status → bar color
const STATUS_COLORS: Record<DeliverableStatus, string> = {
  pending: '#9ca3af',       // gray-400
  in_progress: '#3b82f6',   // blue-500
  in_review: '#eab308',     // yellow-500
  revision: '#f97316',      // orange-500
  approved: '#22c55e',      // green-500
  delivered: '#059669',     // emerald-600
};

function isStale(deliverable: Deliverable, staleDays: number = 3): boolean {
  if (!['in_review', 'revision'].includes(deliverable.status)) return false;
  if (!deliverable.status_changed_at) return false;
  const now = new Date();
  const changed = new Date(deliverable.status_changed_at);
  const diffDays = (now.getTime() - changed.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= staleDays;
}

function isOverdue(deliverable: Deliverable): boolean {
  if (!deliverable.due_date) return false;
  if (deliverable.status === 'delivered' || deliverable.status === 'approved') return false;
  return new Date(deliverable.due_date + 'T00:00:00') < new Date();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function daysAgo(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'today';
  if (diff === 1) return '1 day';
  return `${diff} days`;
}

type GroupBy = 'project' | 'scope' | 'assignee' | 'none';

// Zoom levels: px per day
const ZOOM_LEVELS = [14, 22, 32, 48, 72];
const DEFAULT_ZOOM = 3; // index into ZOOM_LEVELS → 48px

// Get bar start date — use start_date if set, otherwise estimate
function estimateBarStart(d: Deliverable): Date {
  if (d.start_date) {
    return new Date(d.start_date + 'T00:00:00');
  }

  const created = new Date(d.created_at);

  if (!d.due_date) {
    // No due date: bar starts at created_at
    return created;
  }

  const due = new Date(d.due_date + 'T00:00:00');

  // Estimate: work occupies ~14 days before due date
  const estStart = new Date(due);
  estStart.setDate(estStart.getDate() - 14);

  // Use the earlier of created_at and estimated start
  return new Date(Math.min(estStart.getTime(), created.getTime()));
}

// Get bar end date — use due_date if set, otherwise estimate
function estimateBarEnd(d: Deliverable): Date {
  if (d.due_date) {
    return new Date(d.due_date + 'T00:00:00');
  }
  // No due date: show a 7-day placeholder bar
  const start = estimateBarStart(d);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return end;
}

// Check whether either date is estimated (not explicitly set)
function hasEstimatedDates(d: Deliverable): boolean {
  return !d.start_date || !d.due_date;
}

interface DeliverableTimelineProps {
  deliverables: (Deliverable & { project_name?: string })[];
}

export function DeliverableTimeline({ deliverables }: DeliverableTimelineProps) {
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);
  const leftColRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('project');
  const [zoomIdx, setZoomIdx] = useState(DEFAULT_ZOOM);

  const dayWidth = ZOOM_LEVELS[zoomIdx];

  // Sync scroll: body drives header (horizontal) and left column (vertical)
  const handleBodyScroll = useCallback(() => {
    if (!bodyRef.current) return;
    const { scrollLeft, scrollTop } = bodyRef.current;
    if (headerRef.current) headerRef.current.scrollLeft = scrollLeft;
    if (leftColRef.current) leftColRef.current.scrollTop = scrollTop;
  }, []);

  // Compute date range that encompasses all bar starts and ends
  const { startDate, totalDays, today } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Find the earliest bar start and latest bar end
    let earliest = new Date(now);
    earliest.setDate(earliest.getDate() - 7);
    let latest = new Date(now);
    latest.setDate(latest.getDate() + 28);

    for (const d of deliverables) {
      const barStart = estimateBarStart(d);
      const barEnd = estimateBarEnd(d);

      if (barStart < earliest) {
        earliest = new Date(barStart);
      }
      if (barEnd > latest) {
        latest = new Date(barEnd);
      }
    }

    // Pad 5 days on each side
    earliest.setDate(earliest.getDate() - 5);
    latest.setDate(latest.getDate() + 7);

    earliest.setHours(0, 0, 0, 0);
    latest.setHours(0, 0, 0, 0);

    const total = Math.ceil((latest.getTime() - earliest.getTime()) / 86400000);
    return { startDate: earliest, totalDays: total, today: now };
  }, [deliverables]);

  const timelineWidth = totalDays * dayWidth;

  // Generate day markers
  const days = useMemo(() => {
    const result: Date[] = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      result.push(d);
    }
    return result;
  }, [startDate, totalDays]);

  // Group deliverables
  const groups = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: 'All Deliverables', items: deliverables }];
    }

    const map = new Map<string, { label: string; items: typeof deliverables }>();

    for (const d of deliverables) {
      let key: string;
      let label: string;

      if (groupBy === 'project') {
        key = d.project_id;
        label = d.project_name || 'Unknown Project';
      } else if (groupBy === 'assignee') {
        key = d.assigned_to || '__unassigned__';
        label = d.assigned_to || 'Unassigned';
      } else {
        key = d.scope_name || '__none__';
        label = d.scope_name || 'Unassigned';
      }

      if (!map.has(key)) {
        map.set(key, { label, items: [] });
      }
      map.get(key)!.items.push(d);
    }

    return Array.from(map.entries()).map(([key, val]) => ({
      key,
      label: val.label,
      items: val.items,
    }));
  }, [deliverables, groupBy]);

  // Position helpers
  const dayOffset = useCallback((date: Date): number => {
    return Math.floor((date.getTime() - startDate.getTime()) / 86400000);
  }, [startDate]);

  const todayIdx = dayOffset(today);

  // Zoom controls
  const zoomIn = () => setZoomIdx((z) => Math.min(ZOOM_LEVELS.length - 1, z + 1));
  const zoomOut = () => setZoomIdx((z) => Math.max(0, z - 1));
  const scrollToToday = () => {
    if (bodyRef.current) {
      const offset = todayIdx * dayWidth - bodyRef.current.clientWidth / 3;
      bodyRef.current.scrollLeft = Math.max(0, offset);
    }
  };

  // Auto-scroll to today on first render
  const hasAutoScrolled = useRef(false);
  useEffect(() => {
    if (hasAutoScrolled.current || !bodyRef.current || deliverables.length === 0) return;
    hasAutoScrolled.current = true;
    // Small delay to ensure layout is complete
    requestAnimationFrame(() => scrollToToday());
  }, [deliverables.length, scrollToToday]);

  if (deliverables.length === 0) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-2 min-w-0">
        {/* Controls */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 border rounded-md">
            {(['project', 'assignee', 'scope', 'none'] as GroupBy[]).map((g) => (
              <button
                key={g}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                  groupBy === g
                    ? 'bg-primary text-primary-foreground rounded-[4px]'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setGroupBy(g)}
              >
                {g === 'project' ? 'By Project' : g === 'assignee' ? 'By Person' : g === 'scope' ? 'By Discipline' : 'Flat'}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={scrollToToday}>
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={zoomOut}
            disabled={zoomIdx === 0}
            title="Zoom out (wider date range)"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span className="text-[10px] text-muted-foreground w-8 text-center tabular-nums">
            {dayWidth}px
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={zoomIn}
            disabled={zoomIdx === ZOOM_LEVELS.length - 1}
            title="Zoom in (more detail)"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Timeline grid — frozen header + frozen left column + scrollable body */}
        <div className="border rounded-lg bg-card">
          {/* FROZEN HEADER ROW */}
          <div className="flex border-b">
            {/* Top-left corner cell */}
            <div className="w-[220px] shrink-0 border-r bg-muted/30 h-[52px] flex items-end px-3 pb-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Deliverable
              </span>
            </div>
            {/* Date header — synced horizontally from body */}
            <div className="flex-1 overflow-hidden" ref={headerRef}>
              <div style={{ width: timelineWidth, minWidth: '100%' }}>
                <div className="h-[52px] flex flex-col bg-card">
                  {/* Month row */}
                  <div className="h-[26px] flex">
                    {(() => {
                      const months: { label: string; startIdx: number; span: number }[] = [];
                      let currentMonth = '';
                      for (let i = 0; i < days.length; i++) {
                        const m = days[i].toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                        if (m !== currentMonth) {
                          months.push({ label: m, startIdx: i, span: 1 });
                          currentMonth = m;
                        } else {
                          months[months.length - 1].span++;
                        }
                      }
                      return months.map((m) => (
                        <div
                          key={m.label + m.startIdx}
                          className="flex items-center justify-center text-[10px] font-medium text-muted-foreground border-r border-b"
                          style={{ width: m.span * dayWidth }}
                        >
                          {m.label}
                        </div>
                      ));
                    })()}
                  </div>
                  {/* Day row */}
                  <div className="h-[26px] flex">
                    {days.map((d, i) => {
                      const isToday = d.getTime() === today.getTime();
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      const isMonday = d.getDay() === 1;
                      return (
                        <div
                          key={i}
                          className={`flex items-center justify-center border-r ${
                            dayWidth >= 32 ? 'text-[9px]' : 'text-[7px]'
                          } ${
                            isToday
                              ? 'bg-primary/15 text-primary font-bold'
                              : isWeekend
                                ? 'bg-muted/40 text-muted-foreground/50'
                                : isMonday
                                  ? 'text-foreground font-medium'
                                  : 'text-muted-foreground'
                          }`}
                          style={{ width: dayWidth }}
                        >
                          {dayWidth >= 22 ? d.getDate() : (d.getDate() % 5 === 0 || isMonday ? d.getDate() : '')}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SCROLLABLE BODY — max height forces internal scroll */}
          <div className="flex max-h-[600px] overflow-hidden">
            {/* Left column — synced vertically from body */}
            <div className="w-[220px] shrink-0 border-r bg-muted/30 overflow-hidden min-h-0" ref={leftColRef}>
              {groups.map((group) => (
                <div key={group.key}>
                  {groupBy !== 'none' && (
                    <div className="h-7 flex items-center px-3 bg-muted/50 border-b">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-violet-600 truncate">
                        {group.label}
                      </span>
                    </div>
                  )}
                  {group.items.map((d) => (
                    <div
                      key={d.id}
                      className="h-9 flex items-center px-3 border-b cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => navigate(`/projects/${d.project_id}?section=deliverables`)}
                    >
                      <span className="text-xs truncate flex-1">{d.title}</span>
                      {isStale(d) && <AlertTriangle className="h-3 w-3 text-red-500 shrink-0 ml-1" />}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Bar area — scrollable both directions, drives header + left col */}
            <div
              className="flex-1 overflow-auto min-h-0"
              ref={bodyRef}
              onScroll={handleBodyScroll}
            >
              <div style={{ width: timelineWidth, minWidth: '100%' }}>
                {groups.map((group) => (
                  <div key={group.key}>
                    {groupBy !== 'none' && (
                      <div className="h-7 border-b bg-muted/50" />
                    )}
                    {group.items.map((d) => {
                      const stale = isStale(d);
                      const overdue = isOverdue(d);
                      const estimated = hasEstimatedDates(d);

                      // Bar positioning using start_date/due_date with fallback estimation
                      const barStartDate = estimateBarStart(d);
                      const barEndDate = estimateBarEnd(d);

                      const barStartDay = Math.max(0, dayOffset(barStartDate));
                      const barEndDay = Math.min(totalDays, dayOffset(barEndDate) + 1);
                      const barWidthDays = Math.max(1, barEndDay - barStartDay);

                      const barLeftPx = barStartDay * dayWidth;
                      const barWidthPx = Math.max(dayWidth, barWidthDays * dayWidth - 4);

                      return (
                        <div key={d.id} className="h-9 border-b relative flex items-center">
                          {/* Weekend stripes */}
                          {days.map((day, i) => {
                            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                            if (!isWeekend) return null;
                            return (
                              <div
                                key={i}
                                className="absolute top-0 bottom-0 bg-muted/30"
                                style={{ left: i * dayWidth, width: dayWidth }}
                              />
                            );
                          })}

                          {/* Today marker line */}
                          <div
                            className="absolute top-0 bottom-0 z-10 pointer-events-none"
                            style={{ left: todayIdx * dayWidth + Math.floor(dayWidth / 2) }}
                          >
                            <div className="w-0.5 h-full bg-primary/60" />
                          </div>

                          {/* The bar */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={`absolute h-5 rounded-sm cursor-pointer transition-all hover:brightness-110 hover:shadow-md ${
                                  stale ? 'ring-2 ring-red-400 ring-offset-1' : ''
                                } ${estimated ? 'border border-dashed border-gray-400' : ''}`}
                                style={{
                                  left: barLeftPx + 2,
                                  width: barWidthPx,
                                  backgroundColor: STATUS_COLORS[d.status],
                                  opacity: d.status === 'pending' ? 0.5 : 0.85,
                                }}
                                onClick={() => navigate(`/projects/${d.project_id}?section=deliverables`)}
                              >
                                {/* Bar label (only if wide enough) */}
                                {barWidthPx > 80 && (
                                  <span className="absolute inset-0 flex items-center px-1.5 text-[10px] text-white font-medium truncate drop-shadow-sm">
                                    {d.title}
                                  </span>
                                )}

                                {/* Overdue marker */}
                                {overdue && (
                                  <div className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full bg-red-500 border border-white flex items-center justify-center">
                                    <span className="text-[7px] text-white font-bold">!</span>
                                  </div>
                                )}

                                {/* Stale marker */}
                                {stale && !overdue && (
                                  <div className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full bg-amber-500 border border-white flex items-center justify-center">
                                    <AlertTriangle className="h-2 w-2 text-white" />
                                  </div>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs bg-popover text-popover-foreground border shadow-lg p-0">
                              <div className="px-3 py-2 space-y-1">
                                <div className="font-medium text-xs">{d.title}</div>
                                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                  <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[d.status] }} />
                                  <span className="capitalize">{d.status.replace('_', ' ')}</span>
                                  {d.status_changed_at && (
                                    <span>· {daysAgo(d.status_changed_at.split('T')[0])}</span>
                                  )}
                                </div>
                                {d.assigned_to && (
                                  <div className="text-[11px] text-muted-foreground">
                                    Assigned: <span className="text-foreground">{d.assigned_to}</span>
                                  </div>
                                )}
                                {d.reviewer && (
                                  <div className="text-[11px] text-muted-foreground">
                                    Reviewer: <span className="text-foreground">{d.reviewer}</span>
                                  </div>
                                )}
                                {d.start_date && (
                                  <div className="text-[11px] text-muted-foreground">
                                    Start: <span className="text-foreground">{formatDate(d.start_date)}</span>
                                  </div>
                                )}
                                {d.due_date && (
                                  <div className="text-[11px] text-muted-foreground">
                                    Due: <span className={`${overdue ? 'text-red-500 font-medium' : 'text-foreground'}`}>
                                      {formatDate(d.due_date)}{overdue ? ' (overdue)' : ''}
                                    </span>
                                  </div>
                                )}
                                {d.scope_name && (
                                  <div className="text-[11px] text-muted-foreground">
                                    Discipline: <span className="text-foreground capitalize">{d.scope_name}</span>
                                  </div>
                                )}
                                {stale && (
                                  <div className="text-[11px] text-amber-600 font-medium mt-1">
                                    Stale — waiting {daysAgo(d.status_changed_at.split('T')[0])}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 flex-wrap px-1">
          {(Object.entries(STATUS_COLORS) as [DeliverableStatus, string][]).map(([status, color]) => (
            <div key={status} className="flex items-center gap-1.5">
              <div
                className="h-2.5 w-5 rounded-sm"
                style={{ backgroundColor: color, opacity: status === 'pending' ? 0.5 : 0.85 }}
              />
              <span className="text-[10px] text-muted-foreground capitalize">
                {status.replace('_', ' ')}
              </span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <div className="h-2.5 w-5 rounded-sm ring-2 ring-red-400 ring-offset-1 bg-yellow-400" />
            <span className="text-[10px] text-muted-foreground">Stale (3+ days)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-2.5 w-5 rounded-sm border border-dashed border-gray-400 bg-gray-200 opacity-50" />
            <span className="text-[10px] text-muted-foreground">Estimated dates</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-0.5 h-4 bg-primary/60" />
            <span className="text-[10px] text-muted-foreground">Today</span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
